import {RoleNameEnum} from "../../../../utils/types/common";
import {
  TableUserDto,
  UserDto,
  UserPermissionEnum,
} from "../../../../utils/types/user";
import {
  CheckboxesTypes,
  GeneralTypes,
  OtherTypes,
} from "../permission-management-modal/permissionTypes";

export const PERMISSIONS_MANAGEMENT_LIST_PATH = "/admin/permissions/management";

/**
 * Reorders the current page so the acting user is first when they are already
 * in `users`. Permissions Management requests includeSelf so the backend pins
 * Reside admins before pagination; this keeps the row first on the loaded page.
 */
export const pinCurrentUserFirst = <T extends {id?: string}>(
  users: ReadonlyArray<T>,
  currentUserId?: string,
): ReadonlyArray<T> => {
  if (!currentUserId) {
    return users;
  }

  const currentUserIndex = users.findIndex(user => user.id === currentUserId);

  if (currentUserIndex <= 0) {
    return users;
  }

  const currentUser = users[currentUserIndex];

  return [
    currentUser,
    ...users.slice(0, currentUserIndex),
    ...users.slice(currentUserIndex + 1),
  ];
};

export const mapUserDtoToTableUser = (user: UserDto): TableUserDto => ({
  id: user.id,
  firstName: user.firstName,
  lastName: user.lastName,
  email: user.email,
  phone: user.phone,
  role: user.role?.name as RoleNameEnum,
  organization: user.organization ? {name: user.organization.name} : undefined,
  facilities: user.facilities?.map(facility => ({
    id: facility.id,
    name: facility.name,
  })),
  permissions:
    user.permissions?.map(
      permission => permission.name as UserPermissionEnum,
    ) ?? [],
  dashboards: user.dashboards ?? [],
});

export const getManageablePermissionOptions = (includeOther: boolean) => [
  ...GeneralTypes,
  ...CheckboxesTypes,
  ...(includeOther ? OtherTypes : []),
];

/**
 * Selected vs available Quicksight dashboards. Stored via the dashboards API,
 * not as individual entries in `activePermissions`.
 */
export type QuicksightPermissionStatsInput = Readonly<{
  granted: number;
  total: number;
}>;

/**
 * Summary counts for the user-detail header: manageable reporting enums plus
 * optional per-dashboard Quicksight grants.
 */
export const getPermissionStats = (
  activePermissions: ReadonlyArray<UserPermissionEnum>,
  includeOther: boolean,
  quicksight?: QuicksightPermissionStatsInput,
) => {
  const allOptions = getManageablePermissionOptions(includeOther);
  const enumGranted = allOptions.filter(option =>
    activePermissions.includes(option.value),
  ).length;
  const enumTotal = allOptions.length;

  const quicksightGranted = quicksight?.granted ?? 0;
  const quicksightTotal = quicksight?.total ?? 0;

  return {
    granted: enumGranted + quicksightGranted,
    notGranted:
      enumTotal - enumGranted + (quicksightTotal - quicksightGranted),
    groups: includeOther ? 4 : 3, // General, Quicksight, Checkboxes, (+ Other)
  };
};

export type FacilityNamesSummary = Readonly<{
  visibleLabel: string;
  remainingCount: number;
}>;

export const getFacilityNamesSummary = (
  facilities: ReadonlyArray<{name?: string}> | undefined,
  maxVisible = 2,
): FacilityNamesSummary => {
  if (!facilities?.length) {
    return {visibleLabel: "", remainingCount: 0};
  }

  const names = facilities
    .map(facility => facility.name?.trim())
    .filter((name): name is string => Boolean(name));

  if (names.length === 0) {
    return {visibleLabel: "", remainingCount: 0};
  }

  const visibleNames = names.slice(0, maxVisible);

  return {
    visibleLabel: visibleNames.join(", "),
    remainingCount: Math.max(0, names.length - visibleNames.length),
  };
};
